import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Button,
  InputAdornment,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import QRCode from "react-qr-code";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import { shortenAddress } from "../../utils/addressUtils";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { Contact } from "../../models/Contact";
import SearchIcon from "@mui/icons-material/Search";
import { useSecurity } from "../../contexts/SecurityContext";
import { clearMessageParts, sendNewMessage } from "../../redux/messagesSlice";
import { composeMessage, MessagePart } from "../../utils/messageUtils";
import { useConnection } from "../../contexts/ConnectionContext";
import { getReceiversPublicKey } from "../../redux/userSlice";
import { selectContact } from "../../redux/chatListSlice";
import {
  setCurrentNavigationView,
  setSelectedChatView,
} from "../../redux/uiSlice";

interface ReceiveSectionProps {
  ensName?: string;
}
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const ReceiveSection: React.FC<ReceiveSectionProps> = ({ ensName }) => {
  const [amount, setAmount] = React.useState<string>("");
  const [copied, setCopied] = React.useState<boolean>(false);
  const [showQR, setShowQR] = React.useState<boolean>(true);
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedListContact, setSelectedListContact] =
    useState<Contact | null>(null);
  const { privateKey, publicKey } = useSecurity();
  const { connection } = useConnection();
  const { messageParts } = useAppSelector((state) => state.messages);
  const dispatch = useAppDispatch();
  const { receiversPublicKey } = useAppSelector((state) => state.user);
  const { messagingEnabled } = useAppSelector((state) => state.ui);

  const { selectedDenomination, userSelectedAmount } = useAppSelector(
    (state) => state.ui
  );
  const { chainType } = useAppSelector((state) => state.wallet);
  //   const { walletAddress: address } = useAppSelector((state) => state.wallet);
  const { user } = useAppSelector((state) => state.user);
  const [recipientAddress, setRecipientAddress] = useState<string>("");

  const mockTokens = React.useMemo(
    () =>
      chainType === "solana"
        ? [{ symbol: "SOL", balance: 0.0 }]
        : [
            { symbol: "Zviri", balance: 0.0 },
            { symbol: "Vibes", balance: 0.0 },
            { symbol: "Nzou", balance: 0.0 },
            { symbol: "Shanga", balance: 0.0 },
          ],
    [chainType]
  );
  const [selectedToken, setSelectedToken] = useState(mockTokens[0]);
  const { contacts } = useAppSelector((state) => state.contacts);
  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.walletAddress?.includes(searchQuery) ||
      contact.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(user?.walletAddress || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById("receive-qr-code");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `wallet-address-qr-${shortenAddress(
        user?.walletAddress || ""
      )}.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  };

  const generatePaymentURI = () => {
    let uri = `ethereum:${user?.walletAddress}`;
    if (amount) {
      uri += `?value=${amount}`;
    }
    return uri;
  };
  const scrollableBoxStyles = {
    maxHeight: { xs: isMobile ? "80vh" : "68vh", sm: "68vh" },
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  useEffect(() => {
    if (userSelectedAmount) {
      setAmount(userSelectedAmount);
    }
    if (selectedDenomination) {
      setSelectedToken(
        mockTokens.find(
          (token) =>
            token.symbol.toLowerCase() === selectedDenomination.toLowerCase()
        ) || mockTokens[0]
      );
    }
  }, [amount, userSelectedAmount, mockTokens, selectedDenomination]);

  return (
    <Box
      sx={{
        maxWidth: 400,
        mx: "auto",
        textAlign: "center",
        ...scrollableBoxStyles,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Receive Assets
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        {showQR ? (
          <Box sx={{ position: "relative" }}>
            <QRCode
              id="receive-qr-code"
              value={generatePaymentURI()}
              size={200}
              level="H"
              style={{ margin: "0 auto" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                display: "flex",
                gap: 1,
                marginLeft: 2,
              }}
            >
              <Tooltip title="Download QR Code">
                <IconButton onClick={handleDownloadQR} size="small">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share Address">
                <IconButton size="small" onClick={() => setShowQR(false)}>
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" gutterBottom>
              Share your wallet address
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {user?.walletAddress || "No wallet address found"}
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => setShowQR(true)}
            >
              Show QR Code
            </Button>
          </Box>
        )}
      </Paper>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Scan this QR code to receive assets
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Tooltip
          title={copied ? "Copied!" : "Copy to clipboard"}
          placement="top"
        >
          <Chip
            label={ensName || shortenAddress(user?.walletAddress || "")}
            sx={{ mt: 1 }}
            icon={<ContentCopy fontSize="small" />}
            onClick={handleCopyAddress}
            clickable
          />
        </Tooltip>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Request Specific Amount (Optional)
        </Typography>

        <Box sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Request From
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search contacts or enter address"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Contact List */}
          {searchQuery && (
            <List sx={{ maxHeight: 200, overflow: "auto", mt: 1 }}>
              {filteredContacts.map((contact) => (
                <ListItem
                  key={contact.walletAddress}
                  //selected={selectedContact?.walletAddress === contact.walletAddress}
                  onClick={async () => {
                    setSelectedListContact(contact);
                    setRecipientAddress(contact?.walletAddress || "");
                    setSearchQuery(contact.name);

                    await dispatch(
                      getReceiversPublicKey({
                        receiverId: contact.walletAddress || "",
                      })
                    );
                  }}
                >
                  <ListItemText
                    primary={contact.name}
                    secondary={contact.name || contact.walletAddress}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {!selectedListContact && (
            <TextField
              fullWidth
              variant="outlined"
              label="Or enter recipient address"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </Box>

        <TextField
          fullWidth
          type="number"
          variant="outlined"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {selectedToken.symbol}
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 200 }}
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          QR code updates with amount
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Only send supported assets to this address
        </Typography>
      </Box>
      {messagingEnabled && (
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={async () => {
            const newMessageParts: MessagePart[] = [
              ...messageParts,
              {
                type: "token",
                content: `req*${amount}*${selectedToken.symbol}`,
              },
            ];

            try {
              await dispatch(
                sendNewMessage({
                  content: composeMessage(newMessageParts),
                  connection,
                  receiversPublicKey,
                  privateKey,
                  publicKey,
                  selectedContact: selectedListContact,
                  messageParts: newMessageParts,
                  walletAddress: selectedListContact?.walletAddress ?? "",
                })
              ).unwrap();

              dispatch(clearMessageParts());

              if (selectedListContact) {
                dispatch(selectContact({ contact: selectedListContact }));
              }
              dispatch(setSelectedChatView("chat"));
              dispatch(setCurrentNavigationView("chat"));
            } catch (error) {
              console.error("Message send failed:", error);
            }
          }}
          disabled={!amount || (!selectedListContact && !recipientAddress)}
          sx={{ mt: 1, b: 2 }}
        >
          Request {selectedToken.symbol}
        </Button>
      )}
    </Box>
  );
};

export default ReceiveSection;
